import { createAction } from 'redux-actions'

// new search triggered from user action
export const NEW_SEARCH_TRIGGER_MANUAL = 'manual'
// new search triggered from location change
export const NEW_SEARCH_TRIGGER_LOCATION = 'location'

/**
 * @typedef SearchParams
 * @property {'job'|'company'} type - search type
 * @property {string} name - search term
 * @property {string} location - search location
 * @property {number} dist - search location distance
 * @property {number[]} [] filter - search filter
 */

export const newSearch = {
  /**
   * Trigger a new search. Usually dispatched by UI in response to user action.
   * Will dispatch newSearch.request if search is triggered.
   * @function
   * @param {SearchParams} searchParams
   * @param metaParams
   * @param {(NEW_SEARCH_TRIGGER_MANUAL|NEW_SEARCH_TRIGGER_LOCATION)} [metaParams.trigger=NEW_SEARCH_TRIGGER_MANUAL] - who triggered the search
   */
  trigger: createAction(
    'NEW_SEARCH/TRIGGER',
    (searchParams) => searchParams,
    (_, { trigger = NEW_SEARCH_TRIGGER_MANUAL } = {}) => ({ trigger })
  ),
  /**
   * A new search is triggered.
   * Will dispatch newSearch.success or newSearch.failure
   * @function
   * @param {SearchParams} searchParams
   * @param metaParams
   * @param {(NEW_SEARCH_TRIGGER_MANUAL|NEW_SEARCH_TRIGGER_LOCATION)} metaParams.trigger - who triggered the search
   */
  request: createAction(
    'NEW_SEARCH/REQUEST',
    (searchParams) => searchParams,
    (_, { trigger }) => ({ trigger })
  ),
  /**
   * Successful search response.
   * Will dispatch newSearch.fulfill immediately after this action
   * @function
   * @param {Object[]} results - search results, depends on search type
   * @param metaParams
   * @param {SearchParams} metaParams.nextParams - search params for retrieving next page
   * @param {string[]} metaParams.orderOptions - available order option
   * @param {SearchParams} metaParams.queryParams - actual search params used to generate results - may differ from searchParams if some of them are invalid
   * @param {number} metaParams.resultSize - total number of results
   * @param {number} metaParams.resultSizeHighQuality - number of high quality results
   * @param {number} metaParams.resultSizeLowQuality - number of low quality results
   * @param {SearchParams} metaParams.searchParams - search params the search was requested with
   * @param {string|null} metaParams.spellcheck - spellcheck suggestion if available
   */
  success: createAction(
    'NEW_SEARCH/SUCCESS',
    (results) => results,
    (payload, {
      nextParams,
      orderOptions,
      queryParams,
      resultSize,
      resultSizeHighQuality,
      resultSizeLowQuality,
      searchParams,
      resultParams,
      spellcheck
    }) => ({
      nextParams,
      orderOptions,
      resultParams,
      queryParams,
      resultSize,
      resultSizeHighQuality,
      resultSizeLowQuality,
      searchParams,
      spellcheck
    })
  ),
  /**
   * Failure search response.
   * Will dispatch newSearch.fulfill immediately after this action
   * @function
   * @param {Error} error - error message
   * @param metaParams
   * @param {SearchParams} metaParams.searchParams - search params the search was made with
   */
  failure: createAction(
    'NEW_SEARCH/FAILURE',
    (error) => error,
    (payload, { searchParams }) => ({ searchParams })
  ),
  /**
   * Search was fulfilled.
   * @function
   * @param metaParams
   * @param {boolean} metaParams.canceled - true if request was canceled
   * @param {SearchParams} metaParams.searchParams - search params the search was made with
   */
  fulfill: createAction(
    'NEW_SEARCH/FULFILL',
    () => ({}),
    ({ canceled, searchParams }) => ({ canceled, searchParams })
  )
}

export const nextPage = {
  /**
   * Trigger a loading of next results. Usually dispatched by UI in response to user action.
   * Will dispatch nextPage.request if search is triggered.
   * @function
   */
  trigger: createAction(
    'SEARCH_NEXT/TRIGGER',
    () => ({})
  ),
  /**
   * A next page of results is triggered.
   * Will dispatch nextPage.success or nextPage.failure
   * @function
   * @param {SearchParams} requestParams
   */
  request: createAction(
    'SEARCH_NEXT/REQUEST',
    (requestParams) => requestParams
  ),
  /**
   * Successful load of next results page
   * Will dispatch nextPage.fulfill immediately after this action
   * @function
   * @param {Object[]} results - search results, depends on search type
   * @param metaParams
   * @param {number} metaParams.resultSize - total number of results
   * @param {number} metaParams.resultSizeHighQuality - number of high quality results
   * @param {number} metaParams.resultSizeLowQuality - number of low quality results
   * @param {SearchParams} metaParams.requestParams - search params the search was made with
   * @param {SearchParams|null} metaParams.nextParams - search params for next page of results. null if no more pages
   */
  success: createAction(
    'SEARCH_NEXT/SUCCESS',
    (results) => results,
    (payload, {
      resultSize,
      resultSizeHighQuality,
      resultSizeLowQuality,
      requestParams,
      nextParams
    }) => ({
      resultSize,
      resultSizeHighQuality,
      resultSizeLowQuality,
      requestParams,
      nextParams
    })
  ),
  /**
   * Failure loading next results page
   * Will dispatch nextPage.fulfill immediately after this action
   * @function
   * @param {Error} error - error message
   * @param metaParams
   * @param {SearchParams} metaParams.requestParams - search params the search was made with
   */
  failure: createAction(
    'SEARCH_NEXT/FAILURE',
    (error) => error,
    (payload, { requestParams }) => ({ requestParams })
  ),
  /**
   * Loading next results page was fulfilled.
   * @function
   * @param metaParams
   * @param {boolean} metaParams.canceled - true if request was canceled
   * @param {SearchParams} metaParams.requestParams - search params the search was made with
   */
  fulfill: createAction(
    'SEARCH_NEXT/FULFILL',
    () => ({}),
    ({ requestParams, canceled }) => ({ requestParams, canceled })
  )
}
